import React from 'react';
import { FormGroup, FormLabel, FormInput, FormButton } from './FormComponents';
import './ArtistSetupCard.css';

interface ArtistSetupCardProps {
  /** Artist name input value */
  artistName: string;
  /** Callback when artist name changes */
  setArtistName: (name: string) => void;
  /** Whether the artist uses AI in music production */
  artistUsesAI: boolean;
  /** Callback when AI usage selection changes */
  setArtistUsesAI: (usesAI: boolean) => void;
  /** Callback when create artist button is clicked */
  onCreateArtist: () => void;
  /** Whether the artist creation process is in progress */
  creatingArtist: boolean;
  /** Error message to display, if any */
  errorMessage?: string | null;
  /** Custom description text for the card */
  description?: string;
  /** Whether to show as a modal (with overlay) or inline section */
  showAsModal?: boolean;
  /** Callback when modal should be closed (only used when showAsModal=true) */
  onClose?: () => void;
  /** Custom submit button text */
  submitButtonText?: string;
  /** Custom heading text */
  headingText?: string;
}

export const ArtistSetupCard: React.FC<ArtistSetupCardProps> = ({
  artistName,
  setArtistName,
  artistUsesAI,
  setArtistUsesAI,
  onCreateArtist,
  creatingArtist,
  errorMessage,
  description = "Before you can upload tracks or give feedback, please create your artist profile:",
  showAsModal = false,
  onClose,
  submitButtonText = "Create Artist Profile",
  headingText = "Set Up Your Artist Profile"
}) => {
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && artistName.trim() && !creatingArtist) {
      onCreateArtist();
    }
  };

  const cardContent = (
    <div className={`artist-setup-card ${showAsModal ? 'modal-variant' : 'inline-variant'}`}>
      {showAsModal && onClose && (
        <button className="modal-close-button" onClick={onClose}>
          ✕
        </button>
      )}
      
      <h3 className="card-heading">{headingText}</h3>
      <p className="card-description">{description}</p>
      
      <div className="artist-form">
        <FormGroup>
          <FormLabel htmlFor="artist-name">
            Artist Name
          </FormLabel>
          <FormInput
            id="artist-name"
            type="text"
            value={artistName}
            onChange={(e) => setArtistName(e.target.value)}
            placeholder="Enter your artist name"
            maxLength={100}
            disabled={creatingArtist}
            onKeyPress={handleKeyPress}
          />
        </FormGroup>

        <FormGroup className="ai-selection-group">
          <FormLabel>
            Do you use AI in your music production?
          </FormLabel>
          <div className="radio-button-group">
            <label className={`radio-button ${artistUsesAI === true ? 'selected' : ''}`}>
              <input
                type="radio"
                name="usesAI"
                value="yes"
                checked={artistUsesAI === true}
                onChange={() => setArtistUsesAI(true)}
                disabled={creatingArtist}
                className="radio-input"
              />
              <span>Yes</span>
            </label>
            <label className={`radio-button ${artistUsesAI === false ? 'selected' : ''}`}>
              <input
                type="radio"
                name="usesAI"
                value="no"
                checked={artistUsesAI === false}
                onChange={() => setArtistUsesAI(false)}
                disabled={creatingArtist}
                className="radio-input"
              />
              <span>No</span>
            </label>
          </div>
          <small className="ai-help-text">
            Select "Yes" if you use AI tools for composing, generating, or producing your music
          </small>
        </FormGroup>
        
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
        
        <div className="form-actions">
          <FormButton
            type="button"
            onClick={onCreateArtist}
            disabled={!artistName.trim() || creatingArtist}
            variant="primary"
          >
            {creatingArtist ? 'Creating...' : submitButtonText}
          </FormButton>
        </div>
      </div>
    </div>
  );

  if (showAsModal) {
    return (
      <div className="artist-setup-modal-overlay">
        {cardContent}
      </div>
    );
  }

  return (
    <div className="artist-setup-section">
      {cardContent}
    </div>
  );
};